import SkeletonRow from '@/components/UI/molecules/tables/SkeletonRow';
import {styled, useTheme} from '@mui/material/styles';
import {
    TableCellHead,
    TableCellHeadFinal,
    TableCellHeadInitial,
    TableHeadRow,
} from '@/components/UI/molecules/tables/TablePrincipal';
import {fetchGetPurchaseOrder} from '@/components/purchase-order/redux/actions/PurchaceOrderActions';
import {AppDispatch, RootState} from '@config/store';
import {
    Box,
    Button,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
} from '@mui/material';
import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
    IItemSplit,
    ILineDetail,
    IErrorSplit,
    IItemSplitCore,
} from '@/components/purchase-order/confirmPartial/interface';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import {DatePicker} from '@mui/x-date-pickers';
import BtnSplit from '@/components/purchase-order/confirmPartial/BtnSplit';
import BtnReject from '@/components/purchase-order/confirmPartial/BtnReject';
import {useTranslation} from 'react-i18next';

interface IConfirmPurchaseOrderPartialTableProps {
    internalId?: string;
    rejects: {[key: string]: string};
    setRejects: React.Dispatch<
        React.SetStateAction<{
            [key: string]: string;
        }>
    >;
    splits: {[key: string]: IItemSplitCore[]};
    setSplit: React.Dispatch<
        React.SetStateAction<{
            [key: string]: IItemSplitCore[];
        }>
    >;
    lines: {[key: string]: IItemSplit};
    setLines: React.Dispatch<
        React.SetStateAction<{
            [key: string]: IItemSplit;
        }>
    >;
    errorsSplits: IErrorSplit[];
    errorsLines: IErrorSplit[];
    setErrorsLines: React.Dispatch<React.SetStateAction<IErrorSplit[]>>;
    setErrorsSplit: React.Dispatch<React.SetStateAction<IErrorSplit[]>>;
}

const ConfirmPurchaseOrderPartialTableCore: React.FC<IConfirmPurchaseOrderPartialTableProps> = ({
    ...props
}) => {
    const {
        internalId,
        rejects,
        setRejects,
        splits,
        setSplit,
        lines,
        setLines,
        errorsSplits,
        errorsLines,
        setErrorsLines,
        setErrorsSplit,
    } = props;

    const {t, i18n} = useTranslation();
    const theme = useTheme();
    const dispatch: AppDispatch = useDispatch();
    const token = useSelector((state: RootState) => state.logins.accessToken);
    const lineItems = useSelector((state: RootState) => state.purchaceOrderReducers.lineItems);
    const loading = useSelector((state: RootState) => state.purchaceOrderReducers.loading);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const inputLinesRefs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        if (token && internalId) {
            dispatch(fetchGetPurchaseOrder(token, internalId));
        }
    }, [dispatch, token, internalId]);

    const TableContainer = styled(Grid)(({width = 'calc(100vw - 80px)'}: {width?: string}) => ({
        width: width,
    }));

    const [showIsReject, setShowIsReject] = useState<string[]>([]);
    const [showIsSplit, setShowIsSplit] = useState<string[]>([]);

    const handleChangeReject = (event: SelectChangeEvent<string>, id: string) => {
        setRejects((prevRejects) => ({
            ...prevRejects,
            [id]: event.target.value,
        }));
    };

    const getIsReject = (id: string) => {
        return showIsReject.find((item: string) => item === id);
    };

    const getIsSplit = (id: string) => {
        return showIsSplit.find((item: string) => item === id);
    };

    const addIsSplits = (id: string) => {
        setShowIsSplit([...showIsSplit, id]);
        if (!splits[id] || splits[id].length === 0) {
            setSplit((prevSplits) => ({
                ...prevSplits,
                [id]: [
                    {
                        quantitySupplied: 0,
                        deliveryDate: new Date(),
                    },
                ],
            }));
        }
    };

    const addIsRejects = (id: string) => {
        setShowIsReject([...showIsReject, id]);
    };

    const deleteIsRejects = (id: string) => {
        const currentRejects = showIsReject.filter((item: string) => item !== id);
        setShowIsReject(currentRejects);
        setRejects((prevRejects) => {
            const updatedRejects = {...prevRejects};
            delete updatedRejects[id];
            return updatedRejects;
        });
    };

    const onChangeDate = (event: Date | null, itemId: string, currenIndex: number) => {
        if (event === null) return;

        const currentSplit = splits[itemId][currenIndex];

        const currentSplits = splits[itemId].filter((_, index) => index !== currenIndex);

        const newsSplits = [
            ...currentSplits,
            {
                quantitySupplied: currentSplit.quantitySupplied,
                deliveryDate: new Date(event),
            },
        ];

        setSplit((prevSplits) => ({
            ...prevSplits,
            [itemId]: prevSplits[itemId]
                ? newsSplits
                : [
                      {
                          quantitySupplied: 0,
                          deliveryDate: new Date(event),
                          expirationDate: new Date(event),
                          batch: 0,
                          price: 0,
                      },
                  ],
        }));
    };

    const onChangeLineAmount = (event: any, item: ILineDetail, idx: number) => {
        setErrorsLines([]);
        if (event === null) return;

        const currentLine = lines[item.id];

        currentLine.quantitySupplied = Number(event.target.value);

        setLines((prevSplits) => ({
            ...prevSplits,
            [item.id]: currentLine,
        }));
        cuantityErrorLineController(Number(event.target.value), item);
        cuantityLowController(Number(event.target.value), item);
        setTimeout(() => focusInputLine(idx), 100);
    };

    const cuantityLowController = (quantityDelivery: number, item: ILineDetail) => {
        if (quantityDelivery < item.quantity) {
            setShowIsReject([...showIsReject, item.id]);
        } else {
            deleteIsRejects(item.id);
        }
    };

    const cuantityErrorLineController = (quantityDelivery: number, item: ILineDetail) => {
        if (quantityDelivery > item.quantity) {
            const found = errorsLines.find((error) => error.id === item.id);
            if (!found) {
                setErrorsLines([
                    ...errorsLines,
                    {
                        id: item.id,
                        message: t('confirmposimply.quantity_equal_less_requested'),
                    },
                ]);
            }
        } else {
            const errors = errorsLines.filter((error) => error.id !== item.id);
            setErrorsLines(errors);
        }
    };

    const onChangeLineDate = (event: Date | null, itemId: string) => {
        if (event === null) return;

        const currentLine = lines[itemId];

        currentLine.deliveryDate = new Date(event);

        setLines((prevSplits) => ({
            ...prevSplits,
            [itemId]: currentLine,
        }));
    };

    const onChangeAmount = (event: any, item: ILineDetail, currenIndex: number) => {
        if (event === null) return;

        const currentSplit = splits[item.id][currenIndex];

        const other = splits[item.id].map((item, index) => {
            if (index === currenIndex) {
                return {
                    quantitySupplied: Number(event.target.value),
                    deliveryDate: currentSplit.deliveryDate,
                };
            }
            return item;
        });

        setSplit((prevSplits) => ({
            ...prevSplits,
            [item.id]: other,
        }));
        cuantityErrorController(other, item);
        setTimeout(() => focusInput(currenIndex), 100);
    };

    const cuantityErrorController = (split: IItemSplitCore[], item: ILineDetail) => {
        const currentQuantity = split.reduce((carry, quan) => {
            return carry + quan.quantitySupplied;
        }, 0);
        if (currentQuantity > item.quantity) {
            const found = errorsSplits.find((error) => error.id === item.id);
            if (!found) {
                setErrorsSplit([
                    ...errorsSplits,
                    {
                        id: item.id,
                        message: t('confirmposimply.amount_equal_requested'),
                    },
                ]);
            }
        } else {
            const errors = errorsSplits.filter((error) => error.id !== item.id);
            setErrorsSplit(errors);
        }
    };

    const deleteSplit = (item: ILineDetail, currenIndex: number) => {
        const currentSplits = splits[item.id].filter((_, index) => index !== currenIndex);
        setSplit((prevSplits) => ({
            ...prevSplits,
            [item.id]: currentSplits,
        }));
        cuantityErrorController(currentSplits, item);
        if (currentSplits.length === 0) {
            setShowIsSplit(showIsSplit.filter((itemShow) => itemShow !== item.id));
        }
    };

    const addSplits = (itemId: string) => {
        const currentSplits = [
            ...splits[itemId],
            {
                quantitySupplied: 0,
                deliveryDate: new Date(),
            },
        ];
        setSplit((prevSplits) => ({
            ...prevSplits,
            [itemId]: currentSplits,
        }));
    };

    const itemHasSplitError = (itemId: string) => {
        return errorsSplits.find((item) => item.id === itemId);
    };

    const itemHasLinesError = (itemId: string) => {
        const errorItem = errorsLines.find((item) => item.id === itemId);
        if (errorItem) {
            return {
                ...errorItem,
                message: t(errorItem.message),
            };
        }
        return null;
    };

    const focusInput = (index: number) => {
        if (inputRefs.current[index]) {
            inputRefs.current[index]?.focus();
        }
    };

    const focusInputLine = (index: number) => {
        if (inputLinesRefs.current[index]) {
            inputLinesRefs.current[index]?.focus();
        }
    };

    const getIsSplitReject = (item: ILineDetail) => {
        const currentSupplied = splits[item.id].reduce((carry, quan) => {
            return carry + quan.quantitySupplied;
        }, 0);
        return currentSupplied < item.quantity;
    };

    const styleCellData = {padding: '25px 0 25px 20px'};

    return (
        <Box key={i18n.language}>
            {' '}
            <TableContainer>
                <TableHeadRow container>
                    <Grid item xs={0.5}>
                        <TableCellHeadInitial>
                            {t('partial_conf_po.core.table.headers.position')}
                        </TableCellHeadInitial>
                    </Grid>
                    <Grid item xs={2}>
                        <TableCellHead>
                            {t('partial_conf_po.core.table.headers.sku_description')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={1}>
                        <TableCellHead>
                            {t('partial_conf_po.core.table.headers.quantity_requested')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={1}>
                        <TableCellHead>
                            {t('partial_conf_po.core.table.headers.unit')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={1}>
                        <TableCellHead>
                            {t('partial_conf_po.core.table.headers.unit_price')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={2}>
                        <TableCellHead>
                            {t('partial_conf_po.core.table.headers.quantity_suply')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={2}>
                        <TableCellHead>
                            {t('partial_conf_po.core.table.headers.delivery_date')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={1}>
                        <TableCellHead>
                            {t('partial_conf_po.core.table.headers.amount')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={1.5}>
                        <TableCellHeadFinal>
                            {t('partial_conf_po.core.table.headers.actions')}
                        </TableCellHeadFinal>
                    </Grid>
                </TableHeadRow>
                {loading ? (
                    <>
                        <SkeletonRow />
                        <SkeletonRow />
                        <SkeletonRow />
                    </>
                ) : (
                    <Box sx={{height: '65vh', overflowY: 'auto'}}>
                        {lineItems &&
                            lineItems.map((item: ILineDetail, idx: number) => (
                                <Box
                                    key={item.id}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        borderRadius: '0',
                                        borderBottom: '1px solid #C2D1D9',
                                        boxShadow: 'none',
                                        minHeight: '50px',
                                        fontSize: '13px',
                                        fontWeight: 500,
                                        color: '#515151',
                                        background: 'white',
                                        overflowWrap: 'anywhere',
                                    }}
                                >
                                    <Grid
                                        container
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            borderRadius: '0',
                                            boxShadow: 'none',
                                            minHeight: '50px',
                                        }}
                                    >
                                        <Grid item xs={0.5} sx={styleCellData}>
                                            {item.position}
                                        </Grid>
                                        <Grid item xs={2} sx={styleCellData}>
                                            {item.productDetails[0].productName}
                                        </Grid>
                                        <Grid item xs={1} sx={styleCellData}>
                                            {item.quantity}
                                        </Grid>
                                        <Grid item xs={1} sx={styleCellData}>
                                            {item.productDetails[0].unit}
                                        </Grid>
                                        <Grid item xs={1} sx={styleCellData}>
                                            {item.productDetails[0].price}
                                        </Grid>
                                        <Grid item xs={2} sx={styleCellData}>
                                            <TextField
                                                id="outlined-basic"
                                                label={t('partial_conf_po.core.amount')}
                                                size="small"
                                                variant="outlined"
                                                value={lines[item.id]?.quantitySupplied}
                                                onChange={(event) =>
                                                    onChangeLineAmount(event, item, idx)
                                                }
                                                inputRef={(input) =>
                                                    (inputLinesRefs.current[idx] = input)
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={2} sx={styleCellData}>
                                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                <DatePicker
                                                    label={t('partial_conf_po.core.delivery_date')}
                                                    value={lines[item.id]?.deliveryDate}
                                                    onChange={(event) =>
                                                        onChangeLineDate(event, item.id)
                                                    }
                                                    disabled={item.quantityDelivery >= 2}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            sx={{
                                                                '& .MuiInputBase-root': {
                                                                    height: '40px',
                                                                    marginRight: '10px',
                                                                },
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </LocalizationProvider>
                                        </Grid>
                                        <Grid item xs={1} sx={styleCellData}>
                                            {(
                                                lines[item.id]?.quantitySupplied *
                                                item.productDetails[0].price
                                            ).toLocaleString('en-US', {
                                                style: 'currency',
                                                currency: 'USD',
                                                maximumFractionDigits: 2,
                                            })}
                                        </Grid>
                                        <Grid item xs={1.4} sx={{display: 'flex'}}>
                                            {!getIsSplit(item.id) && !getIsReject(item.id) && (
                                                <BtnSplit
                                                    actionFn={() => addIsSplits(item.id)}
                                                    title={t(
                                                        'partial_conf_po.core.partial_delivery'
                                                    )}
                                                />
                                            )}
                                            {!getIsSplit(item.id) && !getIsReject(item.id) && (
                                                <BtnReject
                                                    actionFn={() => addIsRejects(item.id)}
                                                    title={t('decline')}
                                                />
                                            )}
                                        </Grid>
                                    </Grid>
                                    {itemHasLinesError(item.id) && (
                                        <Typography
                                            sx={{
                                                color: '#921A28',
                                                fontSize: '12px',
                                                fontFamily: theme.typography.fontFamily,
                                                fontWeight: 400,
                                                margin: '0 10px 10px 10px',
                                            }}
                                        >
                                            {itemHasLinesError(item.id)?.message}
                                        </Typography>
                                    )}
                                    {item.quantityDelivery >= 2 && (
                                        <Typography
                                            sx={{
                                                color: '#921A28',
                                                fontSize: '12px',
                                                fontFamily: theme.typography.fontFamily,
                                                fontWeight: 400,
                                                margin: '0 10px 10px 10px',
                                            }}
                                        >
                                            {t('confirmposimply.can_only_delivery_twice')}
                                        </Typography>
                                    )}
                                    {getIsReject(item.id) && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                margin: '10px 0 20px 0',
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    color: '#921A28',
                                                    fontSize: '12px',
                                                    fontFamily: theme.typography.fontFamily,
                                                    fontWeight: 400,
                                                    margin: '0 10px 10px 10px',
                                                }}
                                            >
                                                {t('partial_conf_po.core.reject_product')}
                                            </Typography>
                                            <FormControl sx={{width: '400px'}}>
                                                <InputLabel id="demo-simple-select-label">
                                                    {t('partial_conf_po.core.reason_rejection')}
                                                </InputLabel>
                                                <Select
                                                    labelId="demo-simple-select-label"
                                                    id="demo-simple-select"
                                                    value={rejects[item.id]}
                                                    label={t(
                                                        'partial_conf_po.core.reason_rejection'
                                                    )}
                                                    onChange={(event) =>
                                                        handleChangeReject(event, item.id)
                                                    }
                                                >
                                                    <MenuItem value={'Wrong price'}>
                                                        {t(
                                                            'partial_conf_po.core.rejection_list.wrong_price'
                                                        )}
                                                    </MenuItem>
                                                    <MenuItem value={'Product not avilable'}>
                                                        {t(
                                                            'partial_conf_po.core.rejection_list.product_not_available'
                                                        )}
                                                    </MenuItem>
                                                    <MenuItem value={'Out of coverage'}>
                                                        {t(
                                                            'partial_conf_po.core.rejection_list.out_of_coverage'
                                                        )}
                                                    </MenuItem>
                                                    <MenuItem
                                                        value={
                                                            'Product does not match what was offered'
                                                        }
                                                    >
                                                        {t(
                                                            'partial_conf_po.core.rejection_list.product_offered_not_match'
                                                        )}
                                                    </MenuItem>
                                                    <MenuItem value={'Unrecognized product'}>
                                                        {t(
                                                            'partial_conf_po.core.rejection_list.unrecognized_product'
                                                        )}
                                                    </MenuItem>
                                                    <MenuItem value={'Discontinued product'}>
                                                        {t(
                                                            'partial_conf_po.core.rejection_list.discontinued_product'
                                                        )}
                                                    </MenuItem>
                                                    <MenuItem
                                                        value={'Transportation not available'}
                                                    >
                                                        {t(
                                                            'partial_conf_po.core.rejection_list.transpot_not_available'
                                                        )}
                                                    </MenuItem>
                                                    <MenuItem value={'Incomplete inventory'}>
                                                        {t(
                                                            'partial_conf_po.core.rejection_list.incomplete_inventory'
                                                        )}
                                                    </MenuItem>
                                                    <MenuItem value={'Delivery date change'}>
                                                        {t(
                                                            'partial_conf_po.core.rejection_list.delivery_date_change'
                                                        )}
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                            <BtnReject actionFn={() => deleteIsRejects(item.id)} />
                                        </Box>
                                    )}
                                    {getIsSplit(item.id) && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                margin: '10px 0 20px 0',
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    color: theme.palette.primary.main,
                                                    fontSize: '12px',
                                                    fontFamily: theme.typography.fontFamily,
                                                    fontWeight: 400,
                                                    margin: '0 10px 10px 10px',
                                                }}
                                            >
                                                {t('partial_conf_po.core.partial_delivery')}
                                            </Typography>
                                            {splits[item.id] &&
                                                splits[item.id].map((split, index) => {
                                                    return (
                                                        <Box
                                                            key={index}
                                                            sx={{
                                                                margin: '0 10px 10px 10px',
                                                                display: 'flex',
                                                                gap: '10px',
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            <span>
                                                                {t('partial_conf_po.core.delivery')}{' '}
                                                                {index + 1}
                                                            </span>
                                                            <TextField
                                                                id="outlined-basic"
                                                                label={t(
                                                                    'partial_conf_po.core.amount'
                                                                )}
                                                                size="small"
                                                                variant="outlined"
                                                                value={split.quantitySupplied}
                                                                onChange={(event) =>
                                                                    onChangeAmount(
                                                                        event,
                                                                        item,
                                                                        index
                                                                    )
                                                                }
                                                                inputRef={(input) =>
                                                                    (inputRefs.current[index] =
                                                                        input)
                                                                }
                                                            />
                                                            <LocalizationProvider
                                                                dateAdapter={AdapterDateFns}
                                                            >
                                                                <DatePicker
                                                                    label={t(
                                                                        'partial_conf_po.core.delivery_date'
                                                                    )}
                                                                    value={split.deliveryDate}
                                                                    onChange={(event) =>
                                                                        onChangeDate(
                                                                            event,
                                                                            item.id,
                                                                            index
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        item.quantityDelivery >= 2
                                                                    }
                                                                    renderInput={(params) => (
                                                                        <TextField
                                                                            {...params}
                                                                            sx={{
                                                                                '& .MuiInputBase-root':
                                                                                    {
                                                                                        height: '40px',
                                                                                        marginRight:
                                                                                            '10px',
                                                                                    },
                                                                            }}
                                                                        />
                                                                    )}
                                                                />
                                                            </LocalizationProvider>
                                                            <BtnReject
                                                                actionFn={() =>
                                                                    deleteSplit(item, index)
                                                                }
                                                            />
                                                            {splits[item.id].length - 1 === index &&
                                                                getIsSplitReject(item) && (
                                                                    <Button
                                                                        onClick={() =>
                                                                            addSplits(item.id)
                                                                        }
                                                                        color="info"
                                                                        variant="outlined"
                                                                    >
                                                                        {`${t(
                                                                            'partial_conf_po.core.add'
                                                                        )} +`}
                                                                    </Button>
                                                                )}
                                                        </Box>
                                                    );
                                                })}
                                            {getIsSplitReject(item) && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: '',
                                                        margin: '10px 0 20px 20px',
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            color: '#921A28',
                                                            fontSize: '12px',
                                                            fontFamily: theme.typography.fontFamily,
                                                            fontWeight: 400,
                                                            margin: '0 10px 10px 10px',
                                                        }}
                                                    >
                                                        {t('partial_conf_po.core.reject_product')}
                                                    </Typography>
                                                    <FormControl sx={{width: '400px'}}>
                                                        <InputLabel id="demo-simple-select-label">
                                                            {t(
                                                                'partial_conf_po.core.reason_rejection'
                                                            )}
                                                        </InputLabel>
                                                        <Select
                                                            labelId="demo-simple-select-label"
                                                            id="demo-simple-select"
                                                            value={rejects[item.id]}
                                                            label={t(
                                                                'partial_conf_po.core.reason_rejection'
                                                            )}
                                                            onChange={(event) =>
                                                                handleChangeReject(event, item.id)
                                                            }
                                                        >
                                                            <MenuItem value={'Wrong price'}>
                                                                {t(
                                                                    'partial_conf_po.core.rejection_list.wrong_price'
                                                                )}
                                                            </MenuItem>
                                                            <MenuItem
                                                                value={'Product not avilable'}
                                                            >
                                                                {t(
                                                                    'partial_conf_po.core.rejection_list.product_not_available'
                                                                )}
                                                            </MenuItem>
                                                            <MenuItem value={'Out of coverage'}>
                                                                {t(
                                                                    'partial_conf_po.core.rejection_list.out_of_coverage'
                                                                )}
                                                            </MenuItem>
                                                            <MenuItem
                                                                value={
                                                                    'Product does not match what was offered'
                                                                }
                                                            >
                                                                {t(
                                                                    'partial_conf_po.core.rejection_list.product_offered_not_match'
                                                                )}
                                                            </MenuItem>
                                                            <MenuItem
                                                                value={'Unrecognized product'}
                                                            >
                                                                {t(
                                                                    'partial_conf_po.core.rejection_list.unrecognized_product'
                                                                )}
                                                            </MenuItem>
                                                            <MenuItem
                                                                value={'Discontinued product'}
                                                            >
                                                                {t(
                                                                    'partial_conf_po.core.rejection_list.discontinued_product'
                                                                )}
                                                            </MenuItem>
                                                            <MenuItem
                                                                value={
                                                                    'Transportation not available'
                                                                }
                                                            >
                                                                {t(
                                                                    'partial_conf_po.core.rejection_list.transpot_not_available'
                                                                )}
                                                            </MenuItem>
                                                            <MenuItem
                                                                value={'Incomplete inventory'}
                                                            >
                                                                {t(
                                                                    'partial_conf_po.core.rejection_list.incomplete_inventory'
                                                                )}
                                                            </MenuItem>
                                                            <MenuItem
                                                                value={'Delivery date change'}
                                                            >
                                                                {t(
                                                                    'partial_conf_po.core.rejection_list.delivery_date_change'
                                                                )}
                                                            </MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Box>
                                            )}
                                            {itemHasSplitError(item.id) && (
                                                <Typography
                                                    sx={{
                                                        color: '#921A28',
                                                        fontSize: '12px',
                                                        fontFamily: theme.typography.fontFamily,
                                                        fontWeight: 400,
                                                        margin: '0 10px 10px 10px',
                                                    }}
                                                >
                                                    {itemHasSplitError(item.id)?.message}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            ))}
                    </Box>
                )}
            </TableContainer>
        </Box>
    );
};

export default ConfirmPurchaseOrderPartialTableCore;
